import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';
import {
  collection,
  doc,
  onSnapshot,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { useAuth } from '../../../contexts/AuthContext';
import { auth, db, COLLECTIONS, isTeacher, resolveTeacherClassId, Teacher } from '../../../utils/firebase';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';

interface StudentRow {
  uid: string;
  name: string;
  number: number;
}

function toStudentNumber(data: Record<string, unknown>): number {
  const raw = data.number ?? data.studentNumber;
  if (typeof raw === 'number' && !Number.isNaN(raw)) return raw;
  if (typeof raw === 'string') {
    const p = parseInt(raw, 10);
    return Number.isNaN(p) ? 0 : p;
  }
  return 0;
}

/** Firestore 규칙과 맞춘 64자 소문자 hex 문서 ID */
function generateRollingPaperDocId(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function buildPublicUrl(paperId: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/class/public/birthday-rolling-paper/${paperId}`;
}

const BirthdayManagement: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const teacher = currentUser as Teacher;
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [papersByStudentId, setPapersByStudentId] = useState<Map<string, { paperId: string }>>(new Map());
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [creatingFor, setCreatingFor] = useState<string | null>(null);

  const classId = useMemo(() => resolveTeacherClassId(teacher), [teacher]);
  /** 롤링페이퍼 규칙은 request.auth.uid와 일치해야 함(프로필 uid가 문서 ID만 쓰인 경우 불일치 방지) */
  const authUid = auth.currentUser?.uid;
  const canUse = isTeacher(currentUser) && teacher?.schoolId && classId && !!authUid;

  const basePath = useMemo(() => {
    if (!teacher?.schoolId || !classId) return '';
    return `schools/${teacher.schoolId}/classes/${classId}`;
  }, [teacher?.schoolId, classId]);

  useEffect(() => {
    if (!canUse || !basePath) {
      setLoadingStudents(false);
      return;
    }
    const studentsRef = collection(db, `${basePath}/students`);
    const unsub = onSnapshot(query(studentsRef), (snapshot) => {
      const rows: StudentRow[] = snapshot.docs
        .map((d) => {
          const data = d.data();
          if (data.approvalStatus === 'rejected') return null;
          return {
            uid: d.id,
            name: (data.name as string) || '이름 없음',
            number: toStudentNumber(data as Record<string, unknown>),
          };
        })
        .filter((r): r is StudentRow => r !== null)
        .sort((a, b) => a.number - b.number || a.name.localeCompare(b.name));
      setStudents(rows);
      setLoadingStudents(false);
    });
    return () => unsub();
  }, [canUse, basePath]);

  useEffect(() => {
    if (!canUse || !classId || !authUid) return;
    const q = query(
      collection(db, COLLECTIONS.BIRTHDAY_ROLLING_PAPERS),
      where('createdByTeacherUid', '==', authUid),
      where('classId', '==', classId),
      where('status', '==', 'open')
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = new Map<string, { paperId: string; t: number }>();
        snap.docs.forEach((d) => {
          const data = d.data();
          const sid = data.studentId as string;
          if (!sid) return;
          const ca = data.createdAt;
          let t = 0;
          if (ca instanceof Timestamp) t = ca.toMillis();
          else if (ca && typeof (ca as { toMillis?: () => number }).toMillis === 'function') {
            t = (ca as { toMillis: () => number }).toMillis();
          }
          const prev = next.get(sid);
          if (!prev || t >= prev.t) {
            next.set(sid, { paperId: d.id, t });
          }
        });
        const slim = new Map<string, { paperId: string }>();
        next.forEach((v, k) => slim.set(k, { paperId: v.paperId }));
        setPapersByStudentId(slim);
      },
      (err) => {
        console.error('롤링페이퍼 목록 구독 오류:', err);
        toast.error('롤링페이퍼 목록을 불러오지 못했습니다.');
      }
    );
    return () => unsub();
  }, [canUse, classId, authUid]);

  const handleOpenRollingPaper = useCallback(
    async (student: StudentRow) => {
      if (!canUse || !teacher.schoolId || !classId || !authUid) return;
      if (papersByStudentId.has(student.uid)) {
        toast.info('이미 열려 있는 롤링페이퍼 링크가 있습니다. 아래에서 복사하거나 QR을 사용하세요.');
        return;
      }
      setCreatingFor(student.uid);
      try {
        const paperId = generateRollingPaperDocId();
        await setDoc(doc(db, COLLECTIONS.BIRTHDAY_ROLLING_PAPERS, paperId), {
          schoolId: teacher.schoolId,
          classId,
          studentId: student.uid,
          recipientName: student.name,
          ...(student.number > 0 ? { studentNumber: student.number } : {}),
          createdByTeacherUid: authUid,
          createdAt: Timestamp.now(),
          status: 'open',
        });
        toast.success('학생 전용 링크가 만들어졌습니다.');
      } catch (e) {
        console.error(e);
        toast.error('링크를 만들지 못했습니다. 규칙·네트워크를 확인하거나 잠시 후 다시 시도해 주세요.');
      } finally {
        setCreatingFor(null);
      }
    },
    [canUse, teacher, classId, papersByStudentId, authUid]
  );

  const copyUrl = useCallback((paperId: string) => {
    const url = buildPublicUrl(paperId);
    void navigator.clipboard.writeText(url).then(
      () => toast.success('링크를 복사했습니다.'),
      () => toast.error('복사에 실패했습니다. 주소를 직접 선택해 복사해 주세요.')
    );
  }, []);

  if (!canUse) {
    return (
      <div style={{ padding: 'var(--gap-level-4)', maxWidth: 720, margin: '0 auto', fontFamily: 'var(--font-family-primary)' }}>
        <Card padding="24px" background="var(--Color-background-normal)">
          <p style={{ margin: 0, color: 'var(--Color-text-normal)' }}>
            담임 학급이 설정된 교사만 생일 롤링페이퍼를 사용할 수 있습니다.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: 'var(--gap-level-4)',
        maxWidth: 960,
        margin: '0 auto',
        fontFamily: 'var(--font-family-primary)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--gap-level-4)',
          marginBottom: 'var(--gap-level-4)',
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '36px',
            letterSpacing: '-0.02em',
            color: 'var(--Color-text-strong)',
          }}
        >
          생일 관리
        </h1>
        <Button variant="line" size="medium" onClick={() => navigate('/class/teacher/ClassTeacherHome')}>
          대시보드로
        </Button>
      </div>

      <Card padding="24px" borderRadius="16px" background="var(--Color-background-normal)">
        <p
          style={{
            margin: '0 0 16px 0',
            fontSize: '15px',
            lineHeight: 1.6,
            color: 'var(--Color-text-normal)',
          }}
        >
          학생별로 「생일 롤링페이퍼 열기」를 누르면 <strong>로그인 없이</strong> 접속 가능한 전용 링크와 QR이 생깁니다. 링크를 반 친구들에게 공유하면 메시지를 남길 수 있고, 생일을 맞은 학생도 같은 주소로 축하 글을 모아 볼 수 있습니다.
        </p>
      </Card>

      <div style={{ marginTop: 'var(--gap-level-4)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loadingStudents ? (
          <p style={{ color: 'var(--Color-text-normal)' }}>학생 목록을 불러오는 중…</p>
        ) : students.length === 0 ? (
          <Card padding="20px" background="var(--Color-background-normal)">
            <p style={{ margin: 0, color: 'var(--Color-text-normal)' }}>등록된 학생이 없습니다.</p>
          </Card>
        ) : (
          students.map((s) => {
            const paper = papersByStudentId.get(s.uid);
            const url = paper ? buildPublicUrl(paper.paperId) : '';
            const busy = creatingFor === s.uid;
            return (
              <Card key={s.uid} padding="20px" borderRadius="12px" background="var(--Color-background-normal)">
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 16,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--Color-text-strong)' }}>
                      {s.number > 0 ? `${s.number}번 ` : ''}
                      {s.name}
                    </div>
                    {paper ? (
                      <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div
                          style={{
                            wordBreak: 'break-all',
                            fontSize: 13,
                            color: 'var(--Color-text-normal)',
                            maxWidth: '100%',
                          }}
                        >
                          {url}
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                          <Button variant="secondary" size="small" onClick={() => copyUrl(paper.paperId)}>
                            링크 복사
                          </Button>
                          <Button
                            variant="line"
                            size="small"
                            onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
                          >
                            새 탭에서 열기
                          </Button>
                        </div>
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontSize: 13, color: 'var(--Color-text-normal)', marginBottom: 8 }}>QR 코드</div>
                          <div
                            style={{
                              padding: 12,
                              background: 'white',
                              display: 'inline-block',
                              borderRadius: 8,
                              border: '1px solid var(--Color-line-normal)',
                            }}
                          >
                            <QRCodeSVG value={url} size={160} level="M" />
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <Button
                      variant="primary"
                      size="medium"
                      disabled={!!paper || busy}
                      onClick={() => handleOpenRollingPaper(s)}
                    >
                      {paper ? '링크 생성됨' : busy ? '만드는 중…' : '생일 롤링페이퍼 열기'}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

export default BirthdayManagement;
